// Собственик: детайл на имот (само четене) — наематели и история,
// постъпили наеми със СВОЯТА сума и ясна разбивка, нередности, документи.

import React, { useCallback, useState } from 'react';
import { Text, View } from 'react-native';
import { confirmAction, showError } from '../../toast';
import { useFocusEffect } from '@react-navigation/native';
import { api, ISSUE_STATUS_LABELS, money, monthLabel, PAYMENT_STATUS_LABELS } from '../../api';
import { Badge, Button, Card, COLORS, Label, Muted, Row, Screen, Title } from '../../ui';
import { cityLabel } from '../../geo';
import { getLocale, t } from '../../i18n';

export default function OwnerApartmentDetailScreen({ route, navigation }: any) {
  const { id } = route.params;
  const [apartment, setApartment] = useState<any>(null);

  const load = useCallback(() => {
    api(`/owner/apartments/${id}`).then((r) => setApartment(r.apartment)).catch(() => {});
  }, [id]);
  useFocusEffect(load);

  if (!apartment) return <Screen><Muted>{t('Зареждане…')}</Muted></Screen>;

  const terminate = () => {
    confirmAction(
      t('Прекратяване на управлението'),
      t('Имотът ще бъде замразен до прехвърляне. Продължавате ли?'),
      t('Прекрати'),
      async () => {
        try {
          await api(`/management-contracts/${apartment.contract.id}/terminate`, { body: {} });
          navigation.goBack();
        } catch (e: any) {
          showError(e.message);
        }
      }
    );
  };

  return (
    <Screen>
      <Title>{apartment.address}</Title>
      <Muted>{cityLabel(apartment.city, apartment.region)}</Muted>

      {apartment.contract && (
        <Card style={{ marginTop: 12 }}>
          <Label>{t('Управление')}</Label>
          <Row left={t('Пропърти мениджър')} right={apartment.contract.agency} />
          <Row left={t('Процент')} right={`${apartment.managementPercent}%`} />
          <Row left={t('От дата')} right={new Date(apartment.contract.startDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')} />
          {apartment.contract.status === 'ACTIVE' ? (
            <Button title={t('Прекрати управлението')} variant="danger" onPress={terminate} />
          ) : (
            <Badge text={t('Прекратено — имотът е замразен')} color={COLORS.danger} />
          )}
        </Card>
      )}

      {apartment.tenancies.map((tn: any) => (
        <Card key={tn.id}>
          <Label>
            {tn.status === 'ACTIVE' ? t('Текущ наемател') : tn.status === 'INVITED' ? t('Поканен наемател') : t('Минал наемател')}
          </Label>
          <Row left={t('Наемател')} right={tn.tenant ?? t('чака регистрация')} />
          <Row left={t('Наем')} right={t('{amount}/мес.', { amount: money(tn.monthlyRentCents) })} />
          {tn.startDate && <Row left={t('От')} right={new Date(tn.startDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')} />}
          {tn.endDate && <Row left={t('До')} right={new Date(tn.endDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')} />}

          {tn.payments.length > 0 && (
            <>
              <Label>{t('Постъпили наеми (вашата сума)')}</Label>
              {tn.payments.map((p: any) => (
                <View key={p.id} style={{ paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={{ color: COLORS.text }}>{monthLabel(p.periodMonth, p.periodYear)}</Text>
                    <Badge
                      text={t(PAYMENT_STATUS_LABELS[p.status] ?? p.status)}
                      color={p.status === 'PAID' ? COLORS.success : '#D97706'}
                    />
                  </View>
                  <Row left={t('Наем')} right={money(p.amountCents)} />
                  {p.feeCents > 0 && <Row left={t('Такса превод')} right={`−${money(p.feeCents)}`} />}
                  <Row left={t('Пропърти мениджър ({pct}%)', { pct: p.agencyPercent })} right={`−${money(p.agencyShareCents)}`} />
                  <Row left={t('За вас')} right={money(p.ownerShareCents)} />
                </View>
              ))}
            </>
          )}
        </Card>
      ))}

      <Card>
        <Label>{t('Нередности')}</Label>
        {apartment.issues.length === 0 && <Muted>{t('Няма подадени нередности.')}</Muted>}
        {apartment.issues.map((i: any) => (
          <View key={i.id} style={{ paddingVertical: 6 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text style={{ color: COLORS.text, flex: 1 }}>{i.description.slice(0, 60)}</Text>
              <Badge text={t(ISSUE_STATUS_LABELS[i.status] ?? i.status)} />
            </View>
            <Muted>
              {t('Подадена: {date}', { date: new Date(i.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
              {i.resolvedAt ? ` · ${t('Отстранена: {date}', { date: new Date(i.resolvedAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}` : ''}
            </Muted>
          </View>
        ))}
      </Card>

      <Card>
        <Label>{t('Документи (само преглед)')}</Label>
        {apartment.documents.length === 0 && <Muted>{t('Няма документи.')}</Muted>}
        {apartment.documents.map((d: any) => (
          <Row key={d.id} left={d.title} right={new Date(d.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')} />
        ))}
      </Card>
    </Screen>
  );
}
